import type { MouseEventHandler, PropsWithChildren } from 'react';
import styled, { useTheme } from 'styled-components';

import type { MessageType } from '@seuk/design-system/molecules';
import type { SEUKTheme } from '@seuk/design-system/theme';

import type { IconName } from '../../atoms/icon';
import { Icon } from '../../atoms/icon';

export type SmallMessageType = 'info' | 'warning' | 'danger';

export interface SmallMessageProps {
  type: SmallMessageType;
  isCloseButtonShown?: boolean;
  onCloseButtonClick?: () => void;
  className?: string;
}

const getBackgroundColor = (type: MessageType, theme: SEUKTheme) => {
  switch (type) {
    case 'warning':
      return theme.palette.system.subtle.warning;
    case 'danger':
      return theme.palette.system.subtle.negative;
    default:
      return theme.palette.system.subtle.positive;
  }
};

const Container = styled.div<{ $type: SmallMessageType }>`
  display: flex;
  justify-content: space-evenly;
  border-radius: ${({ theme }) => theme.radiusLg};
  overflow: hidden;
  width: 100%;
  background: ${({ $type, theme }) => getBackgroundColor($type, theme)};
`;

const IconContainer = styled.div<{ $type: SmallMessageType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing(4)};
  padding-right: 0;
`;

const TextContainer = styled.div<{ $type: SmallMessageType }>`
  display: flex;
  align-items: center;
  font: ${({ theme }) => theme.typography.paragraph.small.normal};
  padding: ${({ theme }) => theme.spacing(4)};
  flex: 1;
  color: ${({ theme }) => theme.palette.text.onSurface.strong};
`;

const CloseButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(4)};
  padding-left: 0;
  box-sizing: border-box;
  border: none;
  border-radius: 0;
  cursor: pointer;
  background: inherit;
`;

interface IconData {
  name: IconName;
  color: string;
  ariaLabel: string;
}

const getIconData = (type: MessageType, theme: SEUKTheme): IconData => {
  switch (type) {
    case 'warning':
      return {
        name: 'ErrorOutline',
        color: theme.palette.system.strong.warning,
        ariaLabel: 'Warning icon',
      };
    case 'danger':
      return {
        name: 'DangerOutline',
        color: theme.palette.system.strong.negative,
        ariaLabel: 'Danger icon',
      };
    default:
      return {
        name: 'CheckOutline',
        color: theme.palette.system.strong.positive,
        ariaLabel: 'Info icon',
      };
  }
};

export const SmallMessage = ({
  type,
  isCloseButtonShown,
  children,
  onCloseButtonClick,
  className,
}: PropsWithChildren<SmallMessageProps>) => {
  const theme = useTheme();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();

    onCloseButtonClick?.();
  };

  const iconData = getIconData(type, theme);
  return (
    <Container $type={type} className={className}>
      <IconContainer $type={type}>
        <Icon name={iconData.name} color={iconData.color} width="20" height="20" aria-label={iconData.ariaLabel} />
      </IconContainer>
      <TextContainer $type={type}>{children}</TextContainer>
      {isCloseButtonShown && (
        <CloseButton onClick={handleClick} type="button" aria-label="Close">
          <Icon name="Close" width="12" height="12" color={theme.palette.text.onSurface.strong} />
        </CloseButton>
      )}
    </Container>
  );
};
